import React, { FunctionComponent, useEffect, useRef } from 'react'
import c3, { ChartAPI, DataPoint, DataSeries } from 'c3'
import { useChartResize } from './useChartResize'
import './BarSeriesChart.css'

export interface SeriesPoint {
  period: string
  value: number
  hasData: boolean
}

// NO_DATA_COLOR marks a month with no data collected yet, as distinct from a
// real value of 0 — theme-invariant like the real-data bar colours, so it
// doesn't need a dark-mode variant.
const NO_DATA_COLOR = '#9aa1a9'

const isDataPoint = (d: string | DataSeries | DataPoint): d is DataPoint => typeof d === 'object' && 'index' in d

interface Props {
  id: string
  style?: React.CSSProperties
  points: SeriesPoint[]
  average: number
  color: string
  unitLabel: string
  valueFormat?: (value: number) => string
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// monthLabel reads the month directly out of the "YYYY-MM-DD" period string
// (a civil.Date, month-end) rather than via `new Date(...)`, which applies the
// browser's local timezone and can shift a month-end date onto the wrong month.
const monthLabel = (period: string): string => {
  const month = parseInt(period.slice(5, 7), 10)
  return MONTH_LABELS[month - 1] ?? period
}

// NICE_STEP_MULTIPLES are the round step sizes tried, in order, at each
// order of magnitude — e.g. for a peak in the thousands: 1,000, then 2,000,
// then 2,500, then 5,000, then 10,000.
const NICE_STEP_MULTIPLES = [1, 2, 2.5, 5, 10]

// niceAxisMax picks a human-friendly y-axis ceiling and tick step: the
// smallest round step (a multiple of the peak value's order of magnitude)
// that keeps the axis to around targetTicks gridlines, then rounds the
// ceiling up to the next multiple of that step.
const niceAxisMax = (peak: number, targetTicks = 6): { max: number; step: number } => {
  if (peak <= 0) {
    return { max: targetTicks, step: 1 }
  }

  // The `+ 1e-9` guards against floating-point log10 landing just under a
  // whole number (e.g. log10(1000) as 2.9999999999996) and rounding the
  // magnitude down by a factor of 10.
  const magnitude = 10 ** Math.floor(Math.log10(peak) + 1e-9)

  for (const multiple of NICE_STEP_MULTIPLES) {
    const step = magnitude * multiple
    const intervals = Math.ceil(peak / step)
    if (intervals <= targetTicks - 1) {
      return { max: intervals * step, step }
    }
  }

  // Unreachable in practice — the largest multiple (10) always yields at
  // most 1 interval — but keeps this total.
  const step = magnitude * 10
  return { max: Math.ceil(peak / step) * step, step }
}

const axisTicks = (max: number, step: number): number[] => {
  const ticks: number[] = []
  for (let v = 0; v <= max; v += step) {
    ticks.push(v)
  }
  return ticks
}

// BarSeriesChart renders one monthly bar series plus a dashed horizontal
// average line. Bar colour and band label are caller-supplied per metric.
export const BarSeriesChart: FunctionComponent<Props> = ({
  id,
  style,
  points,
  average,
  color,
  unitLabel,
  valueFormat,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ChartAPI | null>(null)
  // the formatter is often an inline function: keep the latest without redrawing the chart for it
  const valueFormatRef = useRef(valueFormat)
  valueFormatRef.current = valueFormat
  // redraw only when the data itself changes, not whenever the parent hands over an equal new array
  const dataKey = JSON.stringify({ points, average, color, unitLabel })

  useChartResize(containerRef, chartRef)

  useEffect(() => {
    if (!containerRef.current) return

    const categories = points.map(p => monthLabel(p.period))
    const values = points.map(p => p.value)
    const formatValue = (v: number): string => (valueFormatRef.current ? valueFormatRef.current(v) : String(v))

    const { max: axisMax, step: axisStep } = niceAxisMax(Math.max(...values, average, 0))

    const chart = c3.generate({
      // Bind directly to the container element, not an "#id" selector — a
      // non-unique id (e.g. two chart instances mounted with the same id
      // prop) would silently make c3 bind to whichever element the selector
      // happens to match first.
      bindto: containerRef.current,
      data: {
        columns: [[unitLabel, ...values]],
        type: 'bar',
        color: (dataColor, d) => (isDataPoint(d) && !points[d.index]?.hasData ? NO_DATA_COLOR : dataColor),
        colors: { [unitLabel]: color },
      },
      axis: {
        x: {
          type: 'category',
          categories,
        },
        y: {
          min: 0,
          max: axisMax,
          padding: { top: 0, bottom: 0 },
          tick: {
            format: formatValue,
            values: axisTicks(axisMax, axisStep),
          },
        },
      },
      grid: {
        y: {
          lines: [{ value: average, class: 'bar-series-average' }],
        },
      },
      bar: {
        width: { ratio: 0.6 },
      },
      resize: { auto: false }, // useChartResize follows the container instead
      legend: {
        show: false,
      },
      tooltip: {
        format: {
          value: (v, _ratio, _id, index) => (points[index]?.hasData ? formatValue(v as number) : 'No data'),
        },
      },
      /** Sets a matching viewBox to scale C3's drawing CSS to prevent clipping in the client print dialogue, */
      onrendered: () => {
        const svg = containerRef.current?.querySelector('svg')
        if (!svg) return

        const width = Number(svg.getAttribute('width'))
        const height = Number(svg.getAttribute('height'))
        if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
          svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
        }
      },
    })

    chartRef.current = chart

    return () => {
      chartRef.current = null
      chart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataKey stands in for the data props
  }, [dataKey])

  // eslint-disable-next-line react/forbid-dom-props -- pass-through component style prop
  return <div ref={containerRef} id={id} className='bar-series-chart' style={style} />
}
